/**
 * RetireConfirmDialog — confirmation modal for retiring a CheckInPromptTemplate.
 *
 * Shows warning that retirement is irreversible and removes the template from rotation.
 * Confirm → calls PATCH retire endpoint (handled by parent).
 * Cancel → dismisses without action.
 */

import type { JSX } from 'react';

import type { PromptTemplate } from '../prompt-library-page.js';

interface Props {
  template: PromptTemplate;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RetireConfirmDialog({ template, onConfirm, onCancel }: Props): JSX.Element {
  const preview =
    template.structuralTemplate.length > 60
      ? template.structuralTemplate.slice(0, 60) + '…'
      : template.structuralTemplate;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-base font-semibold text-gray-900">Retire Template</h2>

        <p className="mt-3 text-sm text-gray-600">Are you sure you want to retire this template?</p>

        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm italic text-gray-700">"{preview}"</p>
        </div>

        <p className="mt-3 text-sm text-gray-600">
          It will no longer appear in check-in rotation.{' '}
          <strong>This action cannot be undone.</strong>
        </p>

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Retire
          </button>
        </div>
      </div>
    </div>
  );
}
